/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner
} from "../"

const getValue = (value, placeholder) => {
  if (value) {
    if (typeof value === 'string') {
      if (value.length > 0) {
        return value
      } else {
        return placeholder
      }
    } else {  // assuming that value is a number
      return value
    }
  } else {
    return placeholder
  }
}

export const Select = (props) => {
  let id = props.id
    ? props.id
    : Math.floor(Math.random() * 1000)

  let options = props.options.length > 0
    ? typeof props.options[0] === 'object'
      ? props.options.map((option, index) => (
          <option
            value={option.value}
            disabled={option.disabled}
            key={`select_option_${id}_${index}`}>
            {option.label}
          </option>
        ))
      : props.options.map((option, index) => (
          <option
            value={option}
            key={`select_option_${id}_${index}`}>
            {option}
          </option>
        ))
    : []

  if (props.default) {
    options.unshift(
      <option key={`select_option_default_${id}`} value=''>
        {props.default}
      </option>
    )
  }

  if (props.placeholder) {
    options.unshift(
      <option hidden key={`select_option_placeholder_${id}`}>
        {props.placeholder}
      </option>
    )
  }

  let style = Object.assign(
    {},
    props.inline ? { borderStyle: 'none', paddingLeft: 0 } : {},
    props.minWidth ? { minWidth: props.minWidth } : {}
  )

  return (
    <div className={`slds-form-element${props.hasError ? ' slds-has-error' : ''} ${props.className ? props.className : ''}`} style={props.style}>
      {props.label &&
        <label
          className='slds-form-element__label'
          htmlFor={id}>
          {props.label}
        </label>
      }
      <div className='slds-form-element__control'>
      <div
        className='slds-select_container'
        style={props.maxWidth
          ? { maxWidth: props.maxWidth }
          : {}
        }>
          <select
            id={id}
            className='slds-select'
            disabled={props.disabled}
            value={getValue(props.value, props.placeholder)}
            onChange={props.onChange}
            style={style}>
            {options}
          </select>
        </div>
      </div>
    </div>
  )
}

Select.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  default: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })),
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  hasError: PropTypes.bool,
  inline: PropTypes.bool,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object
  // TODO: evaluate if props like 'size' and 'title' needs to be added too... or even 'multiple' allowing multiple selections
}

const SelectData = (props) => {
  if (props.data.loading) {
    return <Spinner />
  }

  if (props.data.error) {
    console.log('oops')
  }

  let id = props.id
  ? props.id
  : Math.floor(Math.random() * 1000)

  let options = []
  options = props.data.__type.enumValues.map((option, i) =>
    (
      ((
        props.exclude.length > 0 && !props.exclude.includes(option.name.toLowerCase())
      ) ||
      ( props.exclude.length === 0)) &&
        <option
          key={i}
          value={option.name.toLowerCase()}>
          {option.description}
        </option>
    )
  )
  if (!props.noDefaultOption) {
    options.unshift(
      <option key='default' value={null}>{props.defaultLabel}</option>
    )
  }
  if (props.placeholder) {
    options.unshift(
      <option disabled defaultValue hidden key={`selectData_option_placeholder_${id}`}>
        {props.placeholder}
      </option>
    )
  }

  let style = Object.assign(
    {},
    props.inline ? { borderStyle: 'none', paddingLeft: 0 } : {},
    props.minWidth ? { minWidth: props.minWidth} : {}
  )

  return (
    <div className={`slds-form-element${props.hasError ? ' slds-has-error' : ''} ${props.className ? props.className : ''}`}>
      {props.label &&
        <label
          className='slds-form-element__label'
          htmlFor={id}>
          {props.label}
        </label>
      }
      <div className='slds-form-element__control'>
        <div
          className='slds-select_container'
          style={props.maxWidth
            ? { maxWidth: props.maxWidth }
            : {}
          }>
          <select
            id={id}
            className='slds-select'
            disabled={props.disabled}
            value={props.value
              ? props.value.toLowerCase()
              : props.placeholder ? props.placeholder : undefined
            }
            onChange={props.onChange}
            style={style}>
            {options}
          </select>
        </div>
      </div>
    </div>
  )
}

SelectData.propTypes = {
  id: PropTypes.string,
  label: PropTypes.oneOf([
    PropTypes.string,
    PropTypes.element
  ]),
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  noDefaultOption: PropTypes.bool,
  defaultLabel: PropTypes.string,
  exclude: PropTypes.arrayOf(PropTypes.string),  // only implemented for SelectWithData
  hasError: PropTypes.bool,
  inline: PropTypes.bool,
  minWidth: PropTypes.number,
  data: PropTypes.object.isRequired
}

SelectData.defaultProps = {
  defaultLabel: '',
  noDefaultOption: false,
  hasError: false,
  inline: false,
  exclude: []
}

export const getOptions = gql`
  query($name: String!) {
    __type(name: $name) {
      name
      kind
      enumValues {
        name
        description
      }
    }
  }
`

export const withData =
  graphql(getOptions, {
    options: ({ forField }) => ({
      variables: {
        name: forField
      }
    })
  })

export default withData(SelectData)



// WEBPACK FOOTER //
// ./src/components/select/index.js